import React, { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Truck,
  Phone,
  Calendar,
  Search,
  X,
  CheckCircle2,
  ChevronUp,
  ChevronDown,
  GripVertical,
  ExternalLink,
  RotateCcw,
  UserPlus,
  RefreshCw,
  AlertTriangle,
  Upload,
  FileText,
  Info,
  ArrowUpDown,
  Layers,
  Package,
} from "lucide-react";
import { api, uploadFile, downloadDocument } from "../../lib/api";
import { isAdmin, canAccessDepartment, canViewClientPhone, isDeliveryUser } from "../../lib/permissions";
import { compactMoney } from "../../lib/format";
import { useSavedState } from "../../lib/listState";

function formatDeliveredDateTime(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);

    const day = String(d.getDate()).padStart(2, "0");
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const month = months[d.getMonth()];
    const year = d.getFullYear();

    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return `${day} ${month} ${year}`;
    }

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = String(hours).padStart(2, "0");

    return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return String(val);
  }
}

function OrderDescriptionSnippet({ description, maxLength = 120, testId }) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = (description || "").trim();

  if (!trimmed) {
    return (
      <div
        style={{
          fontSize: 11.5,
          color: "#94a3b8",
          fontStyle: "italic",
          marginTop: 2,
          lineHeight: 1.35,
        }}
        data-testid={testId}
      >
        No description provided
      </div>
    );
  }

  const isLong = trimmed.length > maxLength;
  const displayText = isLong && !expanded ? `${trimmed.slice(0, maxLength)}...` : trimmed;

  return (
    <div
      style={{
        fontSize: 12,
        color: "#475569",
        marginTop: 2,
        lineHeight: 1.4,
        wordBreak: "break-word",
      }}
      data-testid={testId}
      title={trimmed}
    >
      <span>{displayText}</span>
      {isLong && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 600,
            padding: "0 0 0 5px",
            textDecoration: "underline",
          }}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </div>
  );
}

function MiniMetric({ label, value, caption, tone = "blue", active = false, onClick }) {
  return (
    <div
      className={`metric ${tone} ${active ? "active" : ""}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        padding: "14px 16px",
        borderColor: active ? "var(--blue)" : undefined,
      }}
    >
      <div className="metric-top">
        <span>{label}</span>
      </div>
      <strong style={{ fontSize: 22, margin: "6px 0 2px" }}>{value}</strong>
      {caption && <small style={{ fontSize: 11 }}>{caption}</small>}
    </div>
  );
}

export default function DeliveryPersonDetail({
  personId,
  user,
  onBack,
  onOpenOrder,
  onOpenDelivery,
  onChange,
}) {
  const isDelivery = isDeliveryUser(user);
  const canManage = isAdmin(user) || canAccessDepartment(user, ["Production", "Sales"]);

  // Effective person identifier (force current user if Delivery role)
  const targetPersonId = isDelivery ? (user?.name || user?.user_id || personId) : personId;
  const isUnassigned = (targetPersonId || "").toLowerCase() === "unassigned";

  // Persistent filters per delivery person
  const stateKey = `delivery_person_${targetPersonId || "default"}`;
  const [dateFilter, setDateFilter] = useSavedState(stateKey, "dateFilter", "all");
  const [statusFilter, setStatusFilter] = useSavedState(stateKey, "statusFilter", "");
  const [search, setSearch] = useSavedState(stateKey, "search", "");

  const [allOrders, setAllOrders] = useState([]);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Single Order Modals
  const [reassigningOrder, setReassigningOrder] = useState(null);
  const [reassignAgentId, setReassignAgentId] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);

  const [completingOrder, setCompletingOrder] = useState(null);
  const [receivedByInput, setReceivedByInput] = useState("");
  const [deliveryRemarksInput, setDeliveryRemarksInput] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);

  // Grouped Batch Modals
  const [completingGroup, setCompletingGroup] = useState(null);
  const [reassigningGroup, setReassigningGroup] = useState(null);
  const [expandedCompletedBatches, setExpandedCompletedBatches] = useState({});

  const [uploadBusyOrderId, setUploadBusyOrderId] = useState(null);

  // Drag & drop state for active deliveries (works for both batch groups and standalone orders)
  const [draggedStop, setDraggedStop] = useState(null);
  const [dragOverStopKey, setDragOverStopKey] = useState(null);

  // 1. Fetch queue data from backend
  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api("get", "/delivery/queue", {
        params: { timeframe: "all" },
      });
      setAllOrders(res.data?.orders || []);
    } catch (err) {
      console.error("Failed to load delivery queue:", err);
      toast.error("Could not load deliveries");
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Fetch delivery agents for reassignment
  const loadAgents = useCallback(async () => {
    try {
      const res = await api("get", "/users?department=Delivery&active_only=true");
      const list = Array.isArray(res.data) ? res.data : [];
      const validAgents = list.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          ((u.department || "").toLowerCase() === "delivery" || (u.role || "").toLowerCase() === "delivery")
      );
      setDeliveryAgents(validAgents);
    } catch (err) {
      console.error("Failed to load delivery agents:", err);
      setDeliveryAgents([]);
    }
  }, []);

  useEffect(() => {
    loadQueue();
    loadAgents();
  }, [loadQueue, loadAgents]);

  // 3. Resolve Person Details & Orders belonging to this person
  const personData = useMemo(() => {
    const rawTarget = (targetPersonId || "").trim().toLowerCase();

    // Find matched delivery agent if available
    const agentObj = deliveryAgents.find(
      (a) =>
        a.user_id === targetPersonId ||
        (a.name || "").trim().toLowerCase() === rawTarget ||
        (a.name || "").trim().toLowerCase().includes(rawTarget)
    );

    // Filter orders belonging to this driver
    const personOrders = allOrders.filter((o) => {
      if (isUnassigned) {
        return !o.assigned_delivery_person || !o.assigned_delivery_person.trim();
      }

      if (o.delivery_person_id && o.delivery_person_id === targetPersonId) return true;
      if (agentObj && o.delivery_person_id === agentObj.user_id) return true;

      const assigned = (o.assigned_delivery_person || "").trim().toLowerCase();
      if (
        assigned &&
        (assigned === rawTarget || (agentObj && assigned === agentObj.name.toLowerCase()))
      ) {
        return true;
      }

      if (isDelivery && user?.name) {
        return (
          assigned === (user.name || "").toLowerCase() ||
          o.delivery_person_id === user?.user_id
        );
      }

      return false;
    });

    const displayName = isUnassigned
      ? "Unassigned Deliveries"
      : isDelivery
      ? "My Deliveries"
      : agentObj?.name || personOrders[0]?.assigned_delivery_person || targetPersonId || "Delivery Agent";

    const phone = agentObj?.phone || personOrders[0]?.delivery_person_phone || "";

    return {
      displayName,
      phone,
      agentId: agentObj?.user_id || personOrders[0]?.delivery_person_id || "",
      orders: personOrders,
    };
  }, [allOrders, deliveryAgents, targetPersonId, isUnassigned, isDelivery, user]);

  // Helper to determine if an individual order is delivered
  const isDeliveredOrder = useCallback((o) => {
    return (
      o.delivery_status === "Delivered" ||
      o.current_stage === "Delivered" ||
      o.dispatch_status === "Delivered" ||
      o.stage === "Delivered"
    );
  }, []);

  // 4. Group Orders into Delivery Stops (Bulk Requests Grouped into Single Parent Cards)
  const {
    activeDeliveries,
    completedDeliveries,
    totalActiveCount,
    totalActiveOrdersCount,
    totalDeliveredCount,
    todayCount,
    overdueCount,
  } = useMemo(() => {
    const all = personData.orders;

    // Group orders by upload_batch_id
    const batchMap = new Map();
    const standaloneOrders = [];

    all.forEach((o) => {
      const bId = (o.upload_batch_id || "").trim();
      if (bId) {
        if (!batchMap.has(bId)) {
          batchMap.set(bId, []);
        }
        batchMap.get(bId).push(o);
      } else {
        standaloneOrders.push(o);
      }
    });

    const activeStops = [];
    const completedStops = [];

    // Process batch groups: if batch contains multiple orders or is marked bulk upload, group it
    batchMap.forEach((batchOrders, bId) => {
      const isBulkBatch =
        batchOrders.length > 1 ||
        batchOrders.some((o) => o.is_bulk_upload) ||
        bId.startsWith("BATCH-");

      if (!isBulkBatch) {
        // Single order without bulk flag: treat as standalone
        batchOrders.forEach((o) => {
          const item = { isBatch: false, ...o };
          if (isDeliveredOrder(o)) {
            completedStops.push(item);
          } else {
            activeStops.push(item);
          }
        });
        return;
      }

      // Grouped batch delivery item
      const activeInBatch = batchOrders.filter((o) => !isDeliveredOrder(o));
      const deliveredInBatch = batchOrders.filter((o) => isDeliveredOrder(o));
      const allDelivered = activeInBatch.length === 0 && batchOrders.length > 0;
      const isPartiallyDelivered = deliveredInBatch.length > 0 && activeInBatch.length > 0;

      const first = batchOrders[0] || {};
      const pickup_from = first.pickup_from || {};
      const deliver_to = first.deliver_to || {};

      // Determine overall group status
      let overallStatus = "Pending";
      if (allDelivered) {
        overallStatus = "Delivered";
      } else if (
        activeInBatch.some(
          (o) => o.delivery_status === "Out for Delivery" || o.dispatch_status === "Out for Delivery"
        )
      ) {
        overallStatus = isPartiallyDelivered ? "Partially Delivered" : "Out for Delivery";
      } else if (isPartiallyDelivered) {
        overallStatus = "Partially Delivered";
      } else if (activeInBatch.some((o) => o.delivery_status === "Ready for Delivery")) {
        overallStatus = "Ready for Delivery";
      } else {
        overallStatus = activeInBatch[0]?.delivery_status || "Pending";
      }

      // Timeframe priority: overdue > today > tomorrow > upcoming
      let timeframe = "delivered";
      if (!allDelivered) {
        if (activeInBatch.some((o) => o.timeframe === "overdue")) {
          timeframe = "overdue";
        } else if (activeInBatch.some((o) => o.timeframe === "today")) {
          timeframe = "today";
        } else if (activeInBatch.some((o) => o.timeframe === "tomorrow")) {
          timeframe = "tomorrow";
        } else {
          timeframe = activeInBatch[0]?.timeframe || "upcoming";
        }
      }

      // Priority ranking: Urgent > High > Normal
      let priority = "Normal";
      if (batchOrders.some((o) => o.priority === "Urgent")) {
        priority = "Urgent";
      } else if (batchOrders.some((o) => o.priority === "High")) {
        priority = "High";
      }

      // Sequence: lowest sequence integer of active orders
      const sequences = activeInBatch
        .map((o) =>
          typeof o.delivery_sequence === "number" && o.delivery_sequence < 900
            ? o.delivery_sequence
            : 999
        )
        .sort((a, b) => a - b);
      const delivery_sequence = sequences.length > 0 ? sequences[0] : 999;

      const totalQuantity = batchOrders.reduce((sum, o) => sum + (Number(o.quantity) || 0), 0);
      const totalValue = batchOrders.reduce((sum, o) => sum + (Number(o.total_value) || 0), 0);

      const required_delivery_date =
        activeInBatch.find((o) => o.required_delivery_date)?.required_delivery_date ||
        first.required_delivery_date ||
        first.delivery_date ||
        first.target_date ||
        "";

      const has_challan = batchOrders.some((o) => o.has_challan);
      const challan_number = batchOrders.find((o) => o.challan_number)?.challan_number || "";
      const challan_document_id =
        batchOrders.find((o) => o.challan_document_id)?.challan_document_id || "";

      const groupItem = {
        isBatch: true,
        batch_id: bId,
        batch_name: first.upload_batch_name || `Bulk Delivery · ${batchOrders.length} Products`,
        client_name: first.client_name || deliver_to.company_name || "Client",
        company: first.company,
        orders: batchOrders,
        activeOrders: activeInBatch,
        deliveredOrders: deliveredInBatch,
        allDelivered,
        isPartiallyDelivered,
        overallStatus,
        delivery_status: overallStatus,
        timeframe,
        priority,
        delivery_sequence,
        totalQuantity,
        totalValue,
        pickup_from,
        deliver_to,
        required_delivery_date,
        has_challan,
        challan_number,
        challan_document_id,
        assigned_delivery_person: first.assigned_delivery_person || "",
        delivery_person_phone: first.delivery_person_phone || "",
        delivery_person_id: first.delivery_person_id || "",
        delivered_at: batchOrders.find((o) => o.delivered_at)?.delivered_at || "",
        delivered_to:
          batchOrders.find((o) => o.delivered_to || o.received_by)?.received_by ||
          deliver_to.contact_person ||
          "",
      };

      if (allDelivered) {
        completedStops.push(groupItem);
      } else {
        activeStops.push(groupItem);
      }
    });

    // Process standalone single orders
    standaloneOrders.forEach((o) => {
      const item = { isBatch: false, ...o };
      if (isDeliveredOrder(o)) {
        completedStops.push(item);
      } else {
        activeStops.push(item);
      }
    });

    const totalActiveCount = activeStops.length;
    const totalActiveOrdersCount = activeStops.reduce(
      (sum, s) => sum + (s.isBatch ? s.activeOrders.length : 1),
      0
    );
    const totalDeliveredCount =
      completedStops.reduce((sum, s) => sum + (s.isBatch ? s.orders.length : 1), 0) +
      activeStops.reduce((sum, s) => sum + (s.isBatch ? s.deliveredOrders.length : 0), 0);

    const todayCount = activeStops.filter(
      (s) => s.timeframe === "today" || s.timeframe === "overdue"
    ).length;
    const overdueCount = activeStops.filter((s) => s.timeframe === "overdue").length;

    const todayStr = new Date().toISOString().slice(0, 10);

    // Filter helper matching both grouped stops and standalone orders
    const filterFn = (item) => {
      const isDeliv = item.isBatch ? item.allDelivered : isDeliveredOrder(item);
      const itemDate =
        item.required_delivery_date || item.delivery_date || item.delivered_at?.slice(0, 10);

      // Date Filter
      if (dateFilter === "today") {
        if (isDeliv) {
          if (itemDate && itemDate !== todayStr) return false;
        } else {
          if (item.timeframe !== "today" && item.timeframe !== "overdue") return false;
        }
      } else if (dateFilter === "tomorrow") {
        if (isDeliv) return false;
        if (item.timeframe !== "tomorrow") return false;
      } else if (dateFilter === "upcoming") {
        if (isDeliv) return false;
        if (item.timeframe !== "upcoming") return false;
      } else if (dateFilter === "overdue") {
        if (isDeliv) return false;
        if (item.timeframe !== "overdue") return false;
      }

      // Status Filter
      if (statusFilter) {
        const curStatus = (item.delivery_status || "Pending").toLowerCase();
        const sf = statusFilter.toLowerCase();
        if (item.isBatch) {
          const matchesBatchStatus =
            curStatus === sf ||
            item.overallStatus.toLowerCase() === sf ||
            (sf === "out for delivery" &&
              item.activeOrders.some(
                (o) => (o.delivery_status || "").toLowerCase() === "out for delivery"
              )) ||
            (sf === "pending" &&
              item.activeOrders.some((o) =>
                ["pending", "ready for delivery"].includes((o.delivery_status || "").toLowerCase())
              ));
          if (!matchesBatchStatus) return false;
        } else {
          if (curStatus !== sf) return false;
        }
      }

      // Search Filter
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (item.isBatch) {
          const matchBatch =
            (item.batch_id || "").toLowerCase().includes(q) ||
            (item.batch_name || "").toLowerCase().includes(q) ||
            (item.client_name || "").toLowerCase().includes(q) ||
            (item.deliver_to?.company_name || "").toLowerCase().includes(q) ||
            (item.deliver_to?.contact_person || "").toLowerCase().includes(q) ||
            (item.deliver_to?.address || "").toLowerCase().includes(q) ||
            (item.pickup_from?.name || "").toLowerCase().includes(q) ||
            (item.pickup_from?.address || "").toLowerCase().includes(q) ||
            (item.received_by || "").toLowerCase().includes(q) ||
            (item.delivered_to || "").toLowerCase().includes(q) ||
            item.orders.some(
              (o) =>
                (o.order_id || "").toLowerCase().includes(q) ||
                (o.product || "").toLowerCase().includes(q) ||
                (o.description || "").toLowerCase().includes(q)
            );
          if (!matchBatch) return false;
        } else {
          const matchSingle =
            (item.order_id || "").toLowerCase().includes(q) ||
            (item.client_name || "").toLowerCase().includes(q) ||
            (item.deliver_to?.company_name || "").toLowerCase().includes(q) ||
            (item.deliver_to?.contact_person || "").toLowerCase().includes(q) ||
            (item.deliver_to?.address || "").toLowerCase().includes(q) ||
            (item.received_by || "").toLowerCase().includes(q) ||
            (item.delivered_to || "").toLowerCase().includes(q) ||
            (item.pickup_from?.name || "").toLowerCase().includes(q) ||
            (item.pickup_from?.address || "").toLowerCase().includes(q) ||
            (item.product || "").toLowerCase().includes(q) ||
            (item.description || "").toLowerCase().includes(q);
          if (!matchSingle) return false;
        }
      }

      return true;
    };

    // Filtered active and completed stops
    const filteredActive = activeStops.filter(filterFn);
    const filteredCompleted = completedStops.filter(filterFn);

    // Sort Active Deliveries by sequence, timeframe, and ID
    filteredActive.sort((a, b) => {
      const seqA =
        typeof a.delivery_sequence === "number" && a.delivery_sequence < 900
          ? a.delivery_sequence
          : 999;
      const seqB =
        typeof b.delivery_sequence === "number" && b.delivery_sequence < 900
          ? b.delivery_sequence
          : 999;

      if (seqA !== seqB && (seqA < 900 || seqB < 900)) {
        return seqA - seqB;
      }

      // Priority ranking by timeframe
      const timeframeOrder = { overdue: 1, today: 2, tomorrow: 3, upcoming: 4 };
      const tfA = timeframeOrder[a.timeframe] || 5;
      const tfB = timeframeOrder[b.timeframe] || 5;
      if (tfA !== tfB) return tfA - tfB;

      // Status ranking: Out for Delivery comes after pending
      const isOutA = (a.delivery_status || "").includes("Out for Delivery");
      const isOutB = (b.delivery_status || "").includes("Out for Delivery");
      if (isOutA && !isOutB) return 1;
      if (isOutB && !isOutA) return -1;

      const idA = a.isBatch ? a.batch_id : a.order_id;
      const idB = b.isBatch ? b.batch_id : b.order_id;
      return (idA || "").localeCompare(idB || "");
    });

    // Completed deliveries sorted descending by completion / order date
    filteredCompleted.sort((a, b) => {
      const dateA = new Date(
        a.delivered_at || a.delivery_date || a.required_delivery_date || 0
      ).getTime();
      const dateB = new Date(
        b.delivered_at || b.delivery_date || b.required_delivery_date || 0
      ).getTime();
      return dateB - dateA;
    });

    return {
      activeDeliveries: filteredActive,
      completedDeliveries: filteredCompleted,
      totalActiveCount,
      totalActiveOrdersCount,
      totalDeliveredCount,
      todayCount,
      overdueCount,
    };
  }, [personData.orders, isDeliveredOrder, dateFilter, statusFilter, search]);

  // 5. Drag & Drop Reordering for Active Deliveries (Stops)
  const handleDragStart = (e, item, index) => {
    if (!canManage || isUnassigned) return;
    setDraggedStop({ item, index });
    const key = item.isBatch ? item.batch_id : item.order_id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };

  const handleDragOver = (e, targetItem) => {
    if (!canManage || !draggedStop) return;
    e.preventDefault();
    const draggedKey = draggedStop.item.isBatch
      ? draggedStop.item.batch_id
      : draggedStop.item.order_id;
    const targetKey = targetItem.isBatch ? targetItem.batch_id : targetItem.order_id;
    if (draggedKey === targetKey) return;
    setDragOverStopKey(targetKey);
  };

  const handleDragLeave = () => {
    setDragOverStopKey(null);
  };

  const handleDrop = async (e, targetItem, targetIndex) => {
    e.preventDefault();
    setDragOverStopKey(null);

    if (!canManage || !draggedStop) return;
    const sourceIndex = draggedStop.index;
    if (sourceIndex === targetIndex) return;

    const reordered = [...activeDeliveries];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const sequenceItems = [];
    reordered.forEach((stop, idx) => {
      const seq = idx + 1;
      if (stop.isBatch) {
        stop.orders.forEach((o) => {
          sequenceItems.push({ order_id: o.order_id, sequence: seq });
        });
      } else {
        sequenceItems.push({ order_id: stop.order_id, sequence: seq });
      }
    });

    // Optimistic state update
    setAllOrders((prev) =>
      prev.map((ord) => {
        const found = sequenceItems.find((s) => s.order_id === ord.order_id);
        return found ? { ...ord, delivery_sequence: found.sequence } : ord;
      })
    );

    try {
      await api("patch", "/delivery/sequence", {
        delivery_person: personData.displayName,
        items: sequenceItems,
      });
      toast.success(`Delivery sequence updated for ${personData.displayName}`);
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to save new delivery sequence");
      loadQueue();
    } finally {
      setDraggedStop(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedStop(null);
    setDragOverStopKey(null);
  };

  // Move sequence with Up/Down buttons
  const handleMoveSequence = async (currentIndex, direction) => {
    if (!canManage || isUnassigned) return;
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= activeDeliveries.length) return;

    const reordered = [...activeDeliveries];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const sequenceItems = [];
    reordered.forEach((stop, idx) => {
      const seq = idx + 1;
      if (stop.isBatch) {
        stop.orders.forEach((o) => {
          sequenceItems.push({ order_id: o.order_id, sequence: seq });
        });
      } else {
        sequenceItems.push({ order_id: stop.order_id, sequence: seq });
      }
    });

    setAllOrders((prev) =>
      prev.map((ord) => {
        const found = sequenceItems.find((s) => s.order_id === ord.order_id);
        return found ? { ...ord, delivery_sequence: found.sequence } : ord;
      })
    );

    try {
      await api("patch", "/delivery/sequence", {
        delivery_person: personData.displayName,
        items: sequenceItems,
      });
      toast.success(`Sequence updated for Stop #${String(targetIndex + 1).padStart(2, "0")}`);
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to update sequence");
      loadQueue();
    }
  };

  // 6. Quick Status Actions for Single Orders & Groups
  const handleMarkOutForDelivery = async (order) => {
    setAllOrders((prev) =>
      prev.map((ord) =>
        ord.order_id === order.order_id
          ? {
              ...ord,
              delivery_status: "Out for Delivery",
              dispatch_status: "Out for Delivery",
            }
          : ord
      )
    );

    try {
      await api("patch", `/orders/${order.order_id}/delivery-status`, {
        status: "Out for Delivery",
      });
      toast.success(`Order ${order.order_id} is now Out for Delivery`);
      await loadQueue();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to update delivery status");
      loadQueue();
    }
  };

  // Start Group Delivery (marks all eligible active orders in batch Out for Delivery)
  const handleMarkGroupOutForDelivery = async (group) => {
    const batchId = group.batch_id;
    const eligibleOrders = group.activeOrders.filter(
      (o) => o.delivery_status !== "Out for Delivery"
    );
    const orderIds = eligibleOrders.map((o) => o.order_id);

    if (orderIds.length === 0) return;

    setAllOrders((prev) =>
      prev.map((ord) =>
        orderIds.includes(ord.order_id)
          ? {
              ...ord,
              delivery_status: "Out for Delivery",
              dispatch_status: "Out for Delivery",
            }
          : ord
      )
    );

    try {
      await api("patch", `/orders/batch/${batchId}/delivery-status`, {
        status: "Out for Delivery",
        order_ids: orderIds,
      });
      toast.success(`Bulk Batch #${batchId} (${orderIds.length} products) is now Out for Delivery`);
      await loadQueue();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to update batch delivery status");
      loadQueue();
    }
  };

  const handleUploadChallan = async (order, file) => {
    if (!file) return;
    setUploadBusyOrderId(order.order_id);
    try {
      const res = await uploadFile(order.order_id, file, "Challan");
      toast.success("Challan uploaded successfully");
      const doc = res.data || {};
      const docName = doc.name || file.name || "";
      const challanNum = docName.replace(/\.[^/.]+$/, "");

      setAllOrders((prev) =>
        prev.map((ord) =>
          ord.order_id === order.order_id || (order.upload_batch_id && ord.upload_batch_id === order.upload_batch_id)
            ? {
                ...ord,
                has_challan: true,
                challan_document_id: doc.document_id || ord.challan_document_id,
                challan_number: challanNum || ord.challan_number,
              }
            : ord
        )
      );

      if (completingOrder && completingOrder.order_id === order.order_id) {
        setCompletingOrder((prev) => ({
          ...prev,
          has_challan: true,
          challan_document_id: doc.document_id || prev.challan_document_id,
          challan_number: challanNum || prev.challan_number,
        }));
      }

      if (completingGroup && completingGroup.batch_id === order.upload_batch_id) {
        setCompletingGroup((prev) => ({
          ...prev,
          has_challan: true,
          challan_document_id: doc.document_id || prev.challan_document_id,
          challan_number: challanNum || prev.challan_number,
        }));
      }

      loadQueue();
      onChange && onChange();
    } catch (err) {
      console.error("Failed to upload challan:", err);
      const msg = err.response?.data?.detail || "Failed to upload challan";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploadBusyOrderId(null);
    }
  };

  // Submit completion for a single individual order
  const submitCompleteDelivery = async (e) => {
    e.preventDefault();
    if (!completingOrder) return;
    if (!completingOrder.has_challan) {
      toast.error("Please upload a challan before marking this delivery as delivered.");
      return;
    }

    const orderIdToComplete = completingOrder.order_id;
    const recipient =
      receivedByInput.trim() || completingOrder.deliver_to?.contact_person || "Client";
    const remarks = deliveryRemarksInput.trim() || undefined;
    const completionTimestamp = new Date().toISOString();
    const completionDate = completionTimestamp.slice(0, 10);

    setStatusBusy(true);

    // Optimistic instant local update
    setAllOrders((prev) =>
      prev.map((ord) =>
        ord.order_id === orderIdToComplete
          ? {
              ...ord,
              delivery_status: "Delivered",
              current_stage: "Delivered",
              dispatch_status: "Delivered",
              stage: "Delivered",
              delivered_at: completionTimestamp,
              delivery_date: completionDate,
              delivered_to: recipient,
              received_by: recipient,
              delivery_remarks: remarks,
              timeframe: "delivered",
            }
          : ord
      )
    );

    setCompletingOrder(null);
    setReceivedByInput("");
    setDeliveryRemarksInput("");

    try {
      await api("patch", `/orders/${orderIdToComplete}/delivery-status`, {
        status: "Delivered",
        received_by: recipient,
        remarks: remarks,
      });
      toast.success(`Order #${orderIdToComplete} marked as Delivered`);
      await loadQueue();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to complete delivery";
      toast.error(typeof msg === "string" ? msg : "Failed to complete delivery");
      loadQueue();
    } finally {
      setStatusBusy(false);
    }
  };

  // Submit completion for an entire bulk group
  const submitCompleteGroupDelivery = async (e) => {
    e.preventDefault();
    if (!completingGroup) return;
    if (!completingGroup.has_challan) {
      toast.error("Please upload a challan before marking this delivery as delivered.");
      return;
    }

    const batchId = completingGroup.batch_id;
    const recipient =
      receivedByInput.trim() || completingGroup.deliver_to?.contact_person || "Client";
    const remarks = deliveryRemarksInput.trim() || undefined;
    const completionTimestamp = new Date().toISOString();
    const completionDate = completionTimestamp.slice(0, 10);
    const orderIdsToComplete = completingGroup.activeOrders.map((o) => o.order_id);

    setStatusBusy(true);

    // Optimistic instant local update
    setAllOrders((prev) =>
      prev.map((ord) =>
        orderIdsToComplete.includes(ord.order_id)
          ? {
              ...ord,
              delivery_status: "Delivered",
              current_stage: "Delivered",
              dispatch_status: "Delivered",
              stage: "Delivered",
              delivered_at: completionTimestamp,
              delivery_date: completionDate,
              delivered_to: recipient,
              received_by: recipient,
              delivery_remarks: remarks,
              timeframe: "delivered",
            }
          : ord
      )
    );

    setCompletingGroup(null);
    setReceivedByInput("");
    setDeliveryRemarksInput("");

    try {
      await api("patch", `/orders/batch/${batchId}/delivery-status`, {
        status: "Delivered",
        received_by: recipient,
        remarks: remarks,
        order_ids: orderIdsToComplete,
      });
      toast.success(
        `Bulk Batch #${batchId} (${orderIdsToComplete.length} products) marked as Delivered`
      );
      await loadQueue();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to complete delivery";
      toast.error(typeof msg === "string" ? msg : "Failed to complete delivery");
      loadQueue();
    } finally {
      setStatusBusy(false);
    }
  };

  // 7. Reassign Delivery Person (Single Order)
  const openReassignModal = (order) => {
    setReassigningOrder(order);
    setReassignAgentId(order.delivery_person_id || "");
  };

  const submitReassign = async (e) => {
    e.preventDefault();
    if (!reassigningOrder) return;
    setReassignBusy(true);

    const selected = deliveryAgents.find((a) => a.user_id === reassignAgentId);
    const newName = selected ? selected.name : "";
    const newPhone = selected ? selected.phone : "";

    try {
      await api("patch", `/orders/${reassigningOrder.order_id}/assign-delivery`, {
        assigned_delivery_person: newName,
        delivery_person_phone: newPhone,
        delivery_person_id: reassignAgentId,
        remarks: `Reassigned from ${personData.displayName} by ${user?.name || "Admin"}`,
      });
      toast.success(`Order ${reassigningOrder.order_id} assigned to ${newName || "Unassigned"}`);
      setReassigningOrder(null);
      loadQueue();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to reassign delivery agent");
    } finally {
      setReassignBusy(false);
    }
  };

  // Reassign Delivery Person (Bulk Group)
  const openGroupReassignModal = (group) => {
    setReassigningGroup(group);
    setReassignAgentId(group.delivery_person_id || "");
  };

  const submitReassignGroup = async (e) => {
    e.preventDefault();
    if (!reassigningGroup) return;
    setReassignBusy(true);

    const batchId = reassigningGroup.batch_id;
    const selected = deliveryAgents.find((a) => a.user_id === reassignAgentId);
    const newName = selected ? selected.name : "";
    const newPhone = selected ? selected.phone : "";
    const orderIds = reassigningGroup.orders.map((o) => o.order_id);

    // Optimistic local update
    setAllOrders((prev) =>
      prev.map((ord) =>
        orderIds.includes(ord.order_id)
          ? {
              ...ord,
              assigned_delivery_person: newName,
              delivery_person_phone: newPhone,
              delivery_person_id: reassignAgentId,
            }
          : ord
      )
    );

    setReassigningGroup(null);

    try {
      await api("patch", `/orders/batch/${batchId}/assign-delivery`, {
        assigned_delivery_person: newName,
        delivery_person_phone: newPhone,
        delivery_person_id: reassignAgentId,
        order_ids: orderIds,
        remarks: `Reassigned from ${personData.displayName} by ${user?.name || "Admin"}`,
      });
      toast.success(
        `Bulk Batch #${batchId} (${orderIds.length} products) reassigned to ${newName || "Unassigned"}`
      );
      await loadQueue();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to reassign delivery agent");
      loadQueue();
    } finally {
      setReassignBusy(false);
    }
  };

  const toggleExpandCompletedBatch = (batchId) => {
    setExpandedCompletedBatches((prev) => ({
      ...prev,
      [batchId]: !prev[batchId],
    }));
  };

  const hasActiveFilters = dateFilter !== "all" || statusFilter !== "" || search !== "";

  const handleResetFilters = () => {
    setDateFilter("all");
    setStatusFilter("");
    setSearch("");
  };

  const totalAllOrders = totalActiveCount + totalDeliveredCount;

  return (
    <div className="content detail-content" data-testid="delivery-person-detail-page">
      {/* Back Button (Hidden for isolated delivery drivers) */}
      {!isDelivery && (
        <button
          className="back-btn"
          data-testid="delivery-person-back-button"
          onClick={onBack}
          style={{ marginBottom: 12 }}
        >
          <ArrowLeft size={14} /> Back to Delivery
        </button>
      )}

      {/* Driver Header Summary */}
      <div className="detail-head" style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              background: isUnassigned ? "#fef3c7" : "#eff6ff",
              color: isUnassigned ? "#d97706" : "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isUnassigned ? <AlertTriangle size={20} /> : <Truck size={20} />}
          </div>
          <div>
            <div className="small-label">
              {isUnassigned ? "LOGISTICS · UNASSIGNED QUEUE" : "LOGISTICS · DELIVERY AGENT"}
            </div>
            <h1 style={{ margin: "2px 0 4px", fontSize: 22 }}>
              {personData.displayName}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span className="badge slate" style={{ fontSize: 11 }}>
                {isUnassigned ? "Unassigned" : "Delivery Agent"}
              </span>
              {personData.phone && (
                <a
                  href={`tel:${personData.phone}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 13,
                    color: "var(--blue)",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                  title="Call delivery agent"
                >
                  <Phone size={13} /> {personData.phone}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Refresh button */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            className="icon-btn"
            onClick={loadQueue}
            title="Refresh queue"
            data-testid="delivery-person-refresh-btn"
            style={{ width: 36, height: 36 }}
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* Summary Cards Row (Standard Small CRM Metric Cards) */}
      <div className="metrics-grid" style={{ marginBottom: 18 }}>
        <MiniMetric
          label="Active"
          value={totalActiveCount}
          caption={
            totalActiveCount === 1
              ? totalActiveOrdersCount > 1
                ? `1 pending stop (${totalActiveOrdersCount} orders)`
                : "1 pending stop"
              : totalActiveOrdersCount > totalActiveCount
              ? `${totalActiveCount} pending stops (${totalActiveOrdersCount} orders)`
              : `${totalActiveCount} pending stops`
          }
          tone="blue"
          active={statusFilter === "Pending" || statusFilter === "Out for Delivery"}
          onClick={() => setStatusFilter(statusFilter === "Pending" ? "" : "Pending")}
        />
        <MiniMetric
          label="Overdue"
          value={overdueCount}
          caption={overdueCount === 0 ? "None overdue" : overdueCount === 1 ? "1 delayed" : `${overdueCount} delayed`}
          tone="red"
          active={dateFilter === "overdue"}
          onClick={() => setDateFilter(dateFilter === "overdue" ? "all" : "overdue")}
        />
        <MiniMetric
          label="Today"
          value={todayCount}
          caption={todayCount === 0 ? "None scheduled" : todayCount === 1 ? "1 scheduled today" : `${todayCount} scheduled today`}
          tone="purple"
          active={dateFilter === "today"}
          onClick={() => setDateFilter(dateFilter === "today" ? "all" : "today")}
        />
        <MiniMetric
          label="Delivered"
          value={totalDeliveredCount}
          caption={totalDeliveredCount === 1 ? "1 completed delivery" : `${totalDeliveredCount} completed history`}
          tone="green"
          active={statusFilter === "Delivered"}
          onClick={() => setStatusFilter(statusFilter === "Delivered" ? "" : "Delivered")}
        />
      </div>

      {/* Filter Toolbar */}
      <div className="toolbar" data-testid="delivery-person-filter-toolbar" style={{ marginBottom: 18 }}>
        <div className="search-box">
          <Search size={17} />
          <input
            id="person-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Order ID, Client, Product, Address…"
            data-testid="person-search-input"
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setSearch("")}
              title="Clear search"
              data-testid="person-search-clear"
              style={{
                padding: 2,
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Date Filter Dropdown */}
        <select
          id="person-date-filter"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          data-testid="person-date-filter"
        >
          <option value="all">All Dates</option>
          <option value="today">Today &amp; Overdue</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="upcoming">Upcoming</option>
          <option value="overdue">Overdue</option>
        </select>

        {/* Status Filter Dropdown */}
        <select
          id="person-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="person-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending / Ready</option>
          <option value="Out for Delivery">Out for Delivery</option>
          <option value="Delivered">Delivered</option>
        </select>

        {/* Reset Filters */}
        {hasActiveFilters && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={handleResetFilters}
            title="Reset filters"
            data-testid="person-reset-filters"
          >
            <RotateCcw size={13} /> Reset
          </button>
        )}
      </div>

      {loading && allOrders.length === 0 ? (
        <div className="delivery-panel-empty">
          <div className="loading-screen" style={{ margin: "24px auto" }}>
            Loading delivery queue…
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24, marginTop: 10 }}>
          {/* =======================================================
              SECTION 1: ACTIVE DELIVERIES (SHOWN FIRST)
              ======================================================= */}
          <section data-testid="active-deliveries-section">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
                borderBottom: "2px solid #e2e8f0",
                paddingBottom: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
                  Active Deliveries
                </h3>
                <span className="badge blue" style={{ fontSize: 11 }} data-testid="active-deliveries-count-badge">
                  {activeDeliveries.length}
                </span>
                {totalActiveOrdersCount > activeDeliveries.length && (
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    ({totalActiveOrdersCount} items across {activeDeliveries.length} stops)
                  </span>
                )}
              </div>

              {canManage && !isUnassigned && activeDeliveries.length > 1 && (
                <span className="delivery-reorder-hint" style={{ fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <ArrowUpDown size={12} /> Drag cards or use arrows to adjust sequence
                </span>
              )}
            </div>

            {activeDeliveries.length === 0 ? (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                  borderRadius: 8,
                  padding: "24px 20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: 13,
                }}
                data-testid="no-active-deliveries"
              >
                No active deliveries in queue.
              </div>
            ) : (
              <div className="delivery-cards-list" style={{ padding: 0 }}>
                {activeDeliveries.map((item, index) => {
                  const isOverdue = item.timeframe === "overdue";
                  const isOut = (item.delivery_status || "").includes("Out for Delivery");
                  const seqNum = String(index + 1).padStart(2, "0");

                  const itemKey = item.isBatch ? item.batch_id : item.order_id;
                  const isBeingDragged =
                    draggedStop?.item &&
                    (draggedStop.item.isBatch
                      ? draggedStop.item.batch_id === item.batch_id
                      : draggedStop.item.order_id === item.order_id);
                  const isTargeted = dragOverStopKey === itemKey;

                  // ----------------------------------------------------
                  // OPTION A: GROUPED DELIVERY CARD (BULK BATCH)
                  // ----------------------------------------------------
                  if (item.isBatch) {
                    const isPartiallyDelivered = item.isPartiallyDelivered;
                    const allOutForDelivery = item.activeOrders.every(
                      (o) => o.delivery_status === "Out for Delivery"
                    );

                    return (
                      <div
                        key={`batch-${item.batch_id}`}
                        className={`delivery-card delivery-group-card ${isBeingDragged ? "is-dragging" : ""} ${
                          isTargeted ? "drag-over-top" : ""
                        }`}
                        draggable={canManage && !isUnassigned}
                        onDragStart={(e) => handleDragStart(e, item, index)}
                        onDragOver={(e) => handleDragOver(e, item)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, item, index)}
                        onDragEnd={handleDragEnd}
                        data-testid={`active-delivery-card-batch-${item.batch_id}`}
                      >
                        {/* Top Row: Drag Handle, Sequence Pill, Batch Badge, Tag, Client, Qty & Status */}
                        <div className="delivery-card-top">
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {canManage && !isUnassigned && (
                              <div
                                className="delivery-card-drag-handle"
                                title="Drag to reorder delivery sequence"
                                data-testid={`drag-handle-${item.batch_id}`}
                              >
                                <GripVertical size={16} />
                              </div>
                            )}

                            <div
                              className="delivery-seq-pill"
                              style={{
                                background: isOut ? "#2563eb" : isOverdue ? "#dc2626" : "#0f172a",
                              }}
                              title={`Sequence Stop #${seqNum}`}
                              data-testid={`seq-pill-${item.batch_id}`}
                            >
                              #{seqNum}
                            </div>

                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span
                                  className="delivery-group-badge"
                                  title="Bulk Production / Dispatch Batch"
                                  data-testid={`batch-badge-${item.batch_id}`}
                                >
                                  <Layers size={12} /> #{item.batch_id}
                                </span>

                                <span className="badge blue" style={{ fontSize: 11 }}>
                                  Bulk Delivery · {item.orders.length} Products
                                </span>

                                <b style={{ fontSize: 14, color: "var(--navy)" }}>
                                  {item.client_name}
                                </b>

                                {item.priority && item.priority !== "Normal" && (
                                  <span
                                    className={`badge ${item.priority === "Urgent" ? "red" : "amber"}`}
                                    style={{ fontSize: 11 }}
                                  >
                                    {item.priority}
                                  </span>
                                )}
                              </div>

                              <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                                Total Quantity: <b>{(item.totalQuantity || 0).toLocaleString()} units</b>
                                {item.totalValue ? ` · ${compactMoney(item.totalValue)}` : ""}
                                {item.deliver_to?.contact_person && (
                                  <> · Contact: <b>{item.deliver_to.contact_person}</b></>
                                )}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span
                              className={`badge ${
                                isOut
                                  ? "blue"
                                  : isPartiallyDelivered
                                  ? "amber"
                                  : isOverdue
                                  ? "red"
                                  : "amber"
                              }`}
                              style={{ fontSize: 12, padding: "5px 12px" }}
                              data-testid={`delivery-status-${item.batch_id}`}
                            >
                              {isPartiallyDelivered
                                ? `Partially Delivered (${item.deliveredOrders.length}/${item.orders.length})`
                                : item.overallStatus || (isOut ? "Out for Delivery" : "Ready for Delivery")}
                            </span>
                          </div>
                        </div>

                        {/* Consolidated Route Notice: Same Pickup & Destination */}
                        <div
                          className="delivery-group-route-banner"
                          data-testid={`route-banner-${item.batch_id}`}
                        >
                          <Truck size={14} style={{ flexShrink: 0 }} />
                          <span>
                            Single Pickup &amp; Client Destination · All {item.orders.length} products ship together from this warehouse to client
                          </span>
                        </div>

                        {/* Route Grid: Pickup Location vs Delivery Destination */}
                        <div className="delivery-route-grid">
                          {/* Box 1: Pickup Location */}
                          <div className="delivery-pickup-box">
                            <div className="delivery-box-label">
                              Pickup From ({item.pickup_from?.type || "Warehouse"})
                            </div>
                            <div className="delivery-box-name">
                              {item.pickup_from?.name || "Central Warehouse"}
                            </div>
                            <div className="delivery-box-address">
                              {item.pickup_from?.address || "Logistics Hub, GT Karnal Road, Delhi"}
                            </div>
                            <div className="delivery-box-contact">
                              <span>Contact: {item.pickup_from?.contact_person || "Supervisor"}</span>
                              {item.pickup_from?.phone && (
                                <>
                                  <span>·</span>
                                  <a href={`tel:${item.pickup_from.phone}`}>
                                    <Phone size={11} style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                                    {item.pickup_from.phone}
                                  </a>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Box 2: Delivery Location */}
                          <div className="delivery-drop-box">
                            <div className="delivery-box-label">
                              Deliver To (Client Destination)
                            </div>
                            <div className="delivery-box-name">
                              {item.deliver_to?.company_name || item.client_name}
                            </div>
                            <div className="delivery-box-address">
                              {item.deliver_to?.address || "Address on file"}
                            </div>
                            <div className="delivery-box-contact">
                              <span>Contact: {item.deliver_to?.contact_person || "Client Contact"}</span>
                              {canViewClientPhone(user) && item.deliver_to?.phone && (
                                <>
                                  <span>·</span>
                                  <a href={`tel:${item.deliver_to.phone}`}>
                                    <Phone size={11} style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                                    {item.deliver_to.phone}
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Included Products Table */}
                        <div
                          className="delivery-group-products-container"
                          data-testid={`group-products-${item.batch_id}`}
                        >
                          <div className="delivery-group-products-header">
                            <span>INCLUDED PRODUCTS ({item.orders.length})</span>
                            {item.deliveredOrders.length > 0 && (
                              <span className="badge green" style={{ fontSize: 10.5 }}>
                                {item.deliveredOrders.length} of {item.orders.length} Delivered
                              </span>
                            )}
                          </div>

                          <table className="delivery-group-products-table">
                            <thead>
                              <tr>
                                <th style={{ width: 34, textAlign: "center" }}>#</th>
                                <th style={{ width: 120 }}>Order ID</th>
                                <th>Product</th>
                                <th style={{ width: 110 }}>Quantity</th>
                                <th style={{ width: 130 }}>Status</th>
                                <th style={{ textAlign: "right", width: 140 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.orders.map((ord, oIdx) => {
                                const isItemDelivered = isDeliveredOrder(ord);
                                return (
                                  <tr
                                    key={ord.order_id}
                                    data-testid={`batch-item-row-${ord.order_id}`}
                                    style={{ background: isItemDelivered ? "#f0fdf4" : undefined }}
                                  >
                                    <td style={{ textAlign: "center", color: "#94a3b8", fontWeight: 600 }}>
                                      {String(oIdx + 1).padStart(2, "0")}
                                    </td>
                                    <td>
                                      <b
                                        className="mono"
                                        style={{ color: "var(--blue)", cursor: "pointer", fontSize: 12.5 }}
                                        onClick={() => {
                                          if (onOpenOrder) onOpenOrder(ord.order_id);
                                          else if (onOpenDelivery) onOpenDelivery(ord.order_id);
                                        }}
                                        title="View order details"
                                        data-testid={`batch-order-link-${ord.order_id}`}
                                      >
                                        #{ord.order_id}
                                      </b>
                                    </td>
                                    <td>
                                      <div style={{ fontWeight: 600, color: "#1e293b" }}>{ord.product}</div>
                                      {ord.total_value ? (
                                        <div style={{ fontSize: 11, color: "#64748b" }}>
                                          {compactMoney(ord.total_value)}
                                        </div>
                                      ) : null}
                                      <OrderDescriptionSnippet
                                        description={ord.description}
                                        maxLength={75}
                                        testId={`batch-item-description-${ord.order_id}`}
                                      />
                                    </td>
                                    <td>
                                      <b>{(ord.quantity || 0).toLocaleString()}</b> units
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          isItemDelivered
                                            ? "green"
                                            : ord.delivery_status === "Out for Delivery"
                                            ? "blue"
                                            : "amber"
                                        }`}
                                        style={{ fontSize: 11, padding: "2px 7px" }}
                                        data-testid={`batch-item-status-${ord.order_id}`}
                                      >
                                        {isItemDelivered ? "Delivered" : ord.delivery_status || "Pending"}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        {!isItemDelivered ? (
                                          (() => {
                                            const hasItemChallan = Boolean(ord.has_challan || item.has_challan);
                                            return (
                                              <button
                                                type="button"
                                                className="primary-btn compact"
                                                disabled={!hasItemChallan}
                                                style={{
                                                  padding: "3px 8px",
                                                  fontSize: 11,
                                                  background: !hasItemChallan ? "#94a3b8" : "#16a34a",
                                                  borderColor: !hasItemChallan ? "#94a3b8" : "#16a34a",
                                                  color: "#fff",
                                                  cursor: !hasItemChallan ? "not-allowed" : "pointer",
                                                }}
                                                onClick={() => {
                                                  if (!hasItemChallan) {
                                                    toast.error("Please upload a challan before marking this delivery as delivered.");
                                                    return;
                                                  }
                                                  setCompletingOrder(ord);
                                                  setReceivedByInput(
                                                    ord.deliver_to?.contact_person ||
                                                      item.deliver_to?.contact_person ||
                                                      ""
                                                  );
                                                }}
                                                title={
                                                  !hasItemChallan
                                                    ? "Please upload a challan before marking this delivery as delivered."
                                                    : `Mark #${ord.order_id} Delivered`
                                                }
                                                data-testid={`mark-delivered-item-${ord.order_id}`}
                                              >
                                                <CheckCircle2 size={11} /> Mark Delivered
                                              </button>
                                            );
                                          })()
                                        ) : (
                                          <span
                                            style={{
                                              fontSize: 11,
                                              color: "#16a34a",
                                              fontWeight: 700,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 3,
                                            }}
                                          >
                                            <CheckCircle2 size={12} /> Delivered
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          className="outline-btn compact"
                                          style={{ padding: "3px 6px", fontSize: 11 }}
                                          onClick={() => {
                                            if (onOpenDelivery) onOpenDelivery(ord.order_id);
                                            else if (onOpenOrder) onOpenOrder(ord.order_id);
                                          }}
                                          title="View order / delivery details"
                                          data-testid={`open-delivery-item-${ord.order_id}`}
                                        >
                                          <ExternalLink size={11} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Challan Banner */}
                        {item.has_challan ? (
                          <div
                            style={{
                              margin: "6px 0 4px",
                              padding: "6px 12px",
                              background: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                              borderRadius: 6,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              fontSize: 12,
                            }}
                            data-testid={`challan-attached-box-batch-${item.batch_id}`}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#166534" }}>
                              <FileText size={13} />
                              <span>
                                Challan Attached: <b>{item.challan_number || "Document on file"}</b>
                              </span>
                            </div>

                            {item.challan_document_id && (
                              <a
                                href={downloadDocument(item.challan_document_id)}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontSize: 11.5,
                                  color: "#15803d",
                                  fontWeight: 600,
                                  textDecoration: "underline",
                                }}
                              >
                                View Challan
                              </a>
                            )}
                          </div>
                        ) : (
                          <div
                            style={{
                              margin: "8px 0 4px",
                              padding: "8px 12px",
                              background: "#fffbeb",
                              border: "1px solid #fde68a",
                              borderRadius: 6,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              fontSize: 12,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#92400e" }}>
                              <AlertTriangle size={14} color="#b45309" />
                              <span style={{ fontWeight: 600 }}>Please upload a challan before marking this delivery as delivered.</span>
                            </div>
                            <div>
                              <input
                                type="file"
                                id={`challan-upload-batch-${item.batch_id}`}
                                accept=".pdf,image/*,.doc,.docx"
                                style={{ display: "none" }}
                                disabled={uploadBusyOrderId === item.orders[0]?.order_id}
                                onChange={(e) => {
                                  if (e.target.files?.[0] && item.orders[0]) {
                                    handleUploadChallan(item.orders[0], e.target.files[0]);
                                  }
                                  e.target.value = "";
                                }}
                              />
                              <label
                                htmlFor={`challan-upload-batch-${item.batch_id}`}
                                className="primary-btn compact"
                                style={{
                                  background: "#d97706",
                                  borderColor: "#d97706",
                                  color: "#ffffff",
                                  fontSize: 11,
                                  padding: "4px 10px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <Upload size={12} /> {uploadBusyOrderId === item.orders[0]?.order_id ? "Uploading…" : "Upload Challan"}
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Bottom Actions Row */}
                        <div className="delivery-card-actions">
                          <div className="delivery-meta-tags">
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                color: isOverdue ? "var(--red)" : "#475569",
                              }}
                            >
                              <Calendar size={13} />
                              {isOverdue
                                ? `Overdue (Due: ${item.required_delivery_date})`
                                : `Date: ${item.required_delivery_date || "Today"}`}
                            </span>

                            {item.challan_number && (
                              <span className="badge gray" style={{ fontSize: 11 }}>
                                Challan: {item.challan_number}
                              </span>
                            )}

                            <span className="badge slate" style={{ fontSize: 11 }}>
                              Progress: {item.deliveredOrders.length} / {item.orders.length} Delivered
                            </span>
                          </div>

                          <div className="delivery-button-group">
                            {canManage && !isUnassigned && activeDeliveries.length > 1 && (
                              <div style={{ display: "inline-flex", gap: 4 }}>
                                <button
                                  type="button"
                                  className="btn-seq-move"
                                  disabled={index === 0}
                                  onClick={() => handleMoveSequence(index, -1)}
                                  title="Move delivery stop up"
                                  data-testid={`seq-up-batch-${item.batch_id}`}
                                >
                                  <ChevronUp size={13} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-seq-move"
                                  disabled={index === activeDeliveries.length - 1}
                                  onClick={() => handleMoveSequence(index, 1)}
                                  title="Move delivery stop down"
                                  data-testid={`seq-down-batch-${item.batch_id}`}
                                >
                                  <ChevronDown size={13} />
                                </button>
                              </div>
                            )}

                            {!allOutForDelivery && (
                              <button
                                type="button"
                                className="outline-btn compact"
                                onClick={() => handleMarkGroupOutForDelivery(item)}
                                data-testid={`start-delivery-group-${item.batch_id}`}
                                style={{ color: "#2563eb", borderColor: "#93c5fd" }}
                              >
                                <Truck size={13} /> Start Group Delivery
                              </button>
                            )}

                            <button
                              type="button"
                              className="primary-btn compact"
                              disabled={!item.has_challan}
                              onClick={() => {
                                if (!item.has_challan) {
                                  toast.error("Please upload a challan before marking this delivery as delivered.");
                                  return;
                                }
                                setCompletingGroup(item);
                                setReceivedByInput(item.deliver_to?.contact_person || "");
                              }}
                              data-testid={`mark-group-delivered-${item.batch_id}`}
                              style={{
                                background: !item.has_challan ? "#94a3b8" : "#16a34a",
                                borderColor: !item.has_challan ? "#94a3b8" : "#16a34a",
                                color: "#ffffff",
                                cursor: !item.has_challan ? "not-allowed" : "pointer",
                                fontWeight: 700,
                              }}
                              title={
                                !item.has_challan
                                  ? "Please upload a challan before marking this delivery as delivered."
                                  : "Deliver all remaining products in this group"
                              }
                            >
                              <CheckCircle2 size={13} />{" "}
                              {item.isPartiallyDelivered
                                ? `Deliver Remaining (${item.activeOrders.length})`
                                : "Mark Group Delivered"}
                            </button>

                            {canManage && (
                              <button
                                type="button"
                                className="outline-btn compact"
                                onClick={() => openGroupReassignModal(item)}
                                data-testid={`reassign-group-${item.batch_id}`}
                                title="Reassign entire group to another delivery driver"
                              >
                                <UserPlus size={13} /> Reassign Group
                              </button>
                            )}

                            <button
                              type="button"
                              className="outline-btn compact"
                              onClick={() => {
                                if (onOpenDelivery && item.orders[0]) onOpenDelivery(item.orders[0].order_id);
                                else if (onOpenOrder && item.orders[0]) onOpenOrder(item.orders[0].order_id);
                              }}
                              data-testid={`open-delivery-group-${item.batch_id}`}
                            >
                              <ExternalLink size={13} /> Details
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // ----------------------------------------------------
                  // OPTION B: STANDALONE SINGLE ORDER CARD (PRESERVED)
                  // ----------------------------------------------------
                  const order = item;
                  return (
                    <div
                      key={order.order_id}
                      className={`delivery-card ${isBeingDragged ? "is-dragging" : ""} ${
                        isTargeted ? "drag-over-top" : ""
                      }`}
                      draggable={canManage && !isUnassigned}
                      onDragStart={(e) => handleDragStart(e, order, index)}
                      onDragOver={(e) => handleDragOver(e, order)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, order, index)}
                      onDragEnd={handleDragEnd}
                      data-testid={`active-delivery-card-${order.order_id}`}
                    >
                      {/* Top Row: Sequence Pill, Order ID, Client Name, Product & Status Badge */}
                      <div className="delivery-card-top">
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          {/* Drag Handle */}
                          {canManage && !isUnassigned && (
                            <div
                              className="delivery-card-drag-handle"
                              title="Drag to reorder delivery sequence"
                              data-testid={`drag-handle-${order.order_id}`}
                            >
                              <GripVertical size={16} />
                            </div>
                          )}

                          {/* Sequence Pill (#01, #02...) */}
                          <div
                            className="delivery-seq-pill"
                            style={{
                              background: isOut ? "#2563eb" : isOverdue ? "#dc2626" : "#0f172a",
                            }}
                            title={`Sequence Stop #${seqNum}`}
                            data-testid={`seq-pill-${order.order_id}`}
                          >
                            #{seqNum}
                          </div>

                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span
                                className="mono"
                                style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: "var(--blue)",
                                  cursor: "pointer",
                               StrictMode: "true",
                                }}
                                onClick={() => {
                                  if (onOpenOrder) onOpenOrder(order.order_id);
                                  else if (onOpenDelivery) onOpenDelivery(order.order_id);
                                }}
                                title="View order details"
                                data-testid={`order-link-${order.order_id}`}
                              >
                                #{order.order_id}
                              </span>
                              <b style={{ fontSize: 14, color: "var(--navy)" }}>
                                {order.client_name || order.deliver_to?.company_name}
                              </b>

                              {order.priority && (
                                <span
                                  className={`badge ${
                                    order.priority === "Urgent"
                                      ? "red"
                                      : order.priority === "High"
                                      ? "amber"
                                      : "gray"
                                  }`}
                                  style={{ fontSize: 11 }}
                                >
                                  {order.priority}
                                </span>
                              )}
                            </div>

                            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                              {order.product} · <b>{(order.quantity || 0).toLocaleString()} units</b>
                              {order.total_value ? ` · ${compactMoney(order.total_value)}` : ""}
                            </div>

                            <OrderDescriptionSnippet
                              description={order.description}
                              maxLength={120}
                              testId={`active-delivery-description-${order.order_id}`}
                            />
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span
                            className={`badge ${isOut ? "blue" : isOverdue ? "red" : "amber"}`}
                            style={{ fontSize: 12, padding: "5px 12px" }}
                            data-testid={`delivery-status-${order.order_id}`}
                          >
                            {order.delivery_status || (isOut ? "Out for Delivery" : "Pending")}
                          </span>
                        </div>
                      </div>

                      {/* Route Grid: Pickup From vs Deliver To */}
                      <div className="delivery-route-grid">
                        {/* Box 1: Pickup Location */}
                        <div className="delivery-pickup-box">
                          <div className="delivery-box-label">
                            Pickup From ({order.pickup_from?.type || "Warehouse"})
                          </div>
                          <div className="delivery-box-name">
                            {order.pickup_from?.name || "Central Warehouse"}
                          </div>
                          <div className="delivery-box-address">
                            {order.pickup_from?.address || "Logistics Hub, GT Karnal Road, Delhi"}
                          </div>
                          <div className="delivery-box-contact">
                            <span>Contact: {order.pickup_from?.contact_person || "Supervisor"}</span>
                            {order.pickup_from?.phone && (
                              <>
                                <span>·</span>
                                <a href={`tel:${order.pickup_from.phone}`}>
                                  <Phone size={11} style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                                  {order.pickup_from.phone}
                                </a>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Box 2: Delivery Location */}
                        <div className="delivery-drop-box">
                          <div className="delivery-box-label">
                            Deliver To (Client Destination)
                          </div>
                          <div className="delivery-box-name">
                            {order.deliver_to?.company_name || order.client_name}
                          </div>
                          <div className="delivery-box-address">
                            {order.deliver_to?.address || "Address on file"}
                          </div>
                          <div className="delivery-box-contact">
                            <span>Contact: {order.deliver_to?.contact_person || "Client Contact"}</span>
                            {canViewClientPhone(user) && order.deliver_to?.phone && (
                              <>
                                <span>·</span>
                                <a href={`tel:${order.deliver_to.phone}`}>
                                  <Phone size={11} style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                                  {order.deliver_to.phone}
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Challan Requirement Banner & Actions */}
                      {!order.has_challan ? (
                        <div
                          style={{
                            margin: "12px 0 6px",
                            padding: "10px 14px",
                            background: "#fffbeb",
                            border: "1px solid #fde68a",
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                          data-testid={`challan-required-banner-${order.order_id}`}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <AlertTriangle size={15} color="#b45309" style={{ flexShrink: 0 }} />
                            <span style={{ fontSize: 12.5, color: "#92400e", fontWeight: 600 }}>
                              Please upload a challan before marking this delivery as delivered.
                            </span>
                          </div>

                          <div>
                            <input
                              type="file"
                              id={`challan-upload-${order.order_id}`}
                              accept=".pdf,image/*,.doc,.docx"
                              style={{ display: "none" }}
                              disabled={uploadBusyOrderId === order.order_id}
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleUploadChallan(order, e.target.files[0]);
                                }
                                e.target.value = "";
                              }}
                              data-testid={`challan-file-input-${order.order_id}`}
                            />
                            <label
                              htmlFor={`challan-upload-${order.order_id}`}
                              className="primary-btn compact"
                              style={{
                                background: "#d97706",
                                borderColor: "#d97706",
                                color: "#ffffff",
                                cursor: uploadBusyOrderId === order.order_id ? "wait" : "pointer",
                                fontSize: 12,
                                padding: "5px 12px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                              data-testid={`upload-challan-btn-${order.order_id}`}
                            >
                              <Upload size={13} />
                              {uploadBusyOrderId === order.order_id ? "Uploading Challan…" : "Upload Challan"}
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            margin: "8px 0 4px",
                            padding: "6px 12px",
                            background: "#f0fdf4",
                            border: "1px solid #bbf7d0",
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            fontSize: 12,
                          }}
                          data-testid={`challan-attached-box-${order.order_id}`}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#166534" }}>
                            <FileText size={13} />
                            <span>
                              Challan Attached: <b>{order.challan_number || "Document on file"}</b>
                            </span>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            {order.challan_document_id && (
                              <a
                                href={downloadDocument(order.challan_document_id)}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontSize: 11.5,
                                  color: "#15803d",
                                  fontWeight: 600,
                                  textDecoration: "underline",
                                }}
                              >
                                View Challan
                              </a>
                            )}
                            <input
                              type="file"
                              id={`challan-reupload-${order.order_id}`}
                              accept=".pdf,image/*,.doc,.docx"
                              style={{ display: "none" }}
                              disabled={uploadBusyOrderId === order.order_id}
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleUploadChallan(order, e.target.files[0]);
                                }
                                e.target.value = "";
                              }}
                            />
                            <label
                              htmlFor={`challan-reupload-${order.order_id}`}
                              style={{
                                fontSize: 11,
                                color: "#4b5563",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                              title="Re-upload Challan"
                            >
                              Re-upload
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Bottom Actions Row */}
                      <div className="delivery-card-actions">
                        {/* Left: Meta tags (Target Date, Challan) */}
                        <div className="delivery-meta-tags">
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontSize: 12,
                              fontWeight: 600,
                              color: isOverdue ? "var(--red)" : "#475569",
                            }}
                          >
                            <Calendar size={13} />
                            {isOverdue
                              ? `Overdue (Due: ${order.required_delivery_date})`
                              : `Date: ${order.required_delivery_date || "Today"}`}
                          </span>

                          {order.challan_number && (
                            <span className="badge gray" style={{ fontSize: 11 }}>
                              Challan: {order.challan_number}
                            </span>
                          )}
                        </div>

                        {/* Right: Action Buttons */}
                        <div className="delivery-button-group">
                          {/* Sequence Move Up / Move Down buttons */}
                          {canManage && !isUnassigned && activeDeliveries.length > 1 && (
                            <div style={{ display: "inline-flex", gap: 4 }}>
                              <button
                                type="button"
                                className="btn-seq-move"
                                disabled={index === 0}
                                onClick={() => handleMoveSequence(index, -1)}
                                title="Move delivery stop up"
                                data-testid={`seq-up-${order.order_id}`}
                              >
                                <ChevronUp size={13} />
                              </button>
                              <button
                                type="button"
                                className="btn-seq-move"
                                disabled={index === activeDeliveries.length - 1}
                                onClick={() => handleMoveSequence(index, 1)}
                                title="Move delivery stop down"
                                data-testid={`seq-down-${order.order_id}`}
                              >
                                <ChevronDown size={13} />
                              </button>
                            </div>
                          )}

                          {/* Driver / Manager: Start Delivery */}
                          {!isOut && (
                            <button
                              type="button"
                              className="outline-btn compact"
                              onClick={() => handleMarkOutForDelivery(order)}
                              data-testid={`start-delivery-${order.order_id}`}
                              style={{ color: "#2563eb", borderColor: "#93c5fd" }}
                            >
                              <Truck size={13} /> Start Delivery
                            </button>
                          )}

                          {/* Driver / Manager: Mark Delivered */}
                          <button
                            type="button"
                            className="primary-btn compact"
                            disabled={!order.has_challan}
                            onClick={() => {
                              if (!order.has_challan) {
                                toast.error("Please upload a challan before marking this delivery as delivered.");
                                return;
                              }
                              setCompletingOrder(order);
                              setReceivedByInput(order.deliver_to?.contact_person || "");
                            }}
                            data-testid={`mark-delivered-${order.order_id}`}
                            style={{
                              background: !order.has_challan ? "#94a3b8" : "#16a34a",
                              borderColor: !order.has_challan ? "#94a3b8" : "#16a34a",
                              color: "#ffffff",
                              cursor: !order.has_challan ? "not-allowed" : "pointer",
                            }}
                            title={
                              !order.has_challan
                                ? "Please upload a challan before marking this delivery as delivered."
                                : "Complete delivery"
                            }
                          >
                            <CheckCircle2 size={13} /> Mark Delivered
                          </button>

                          {/* Reassign Agent button for Admins / Production */}
                          {canManage && (
                            <button
                              type="button"
                              className="outline-btn compact"
                              onClick={() => openReassignModal(order)}
                              data-testid={`reassign-agent-${order.order_id}`}
                              title="Reassign to another delivery driver"
                            >
                              <UserPlus size={13} /> Reassign
                            </button>
                          )}

                          {/* Open Delivery Detail */}
                          <button
                            type="button"
                            className="outline-btn compact"
                            onClick={() => onOpenDelivery && onOpenDelivery(order.order_id)}
                            data-testid={`open-delivery-${order.order_id}`}
                          >
                            <ExternalLink size={13} /> Details
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* =======================================================
              SECTION 2: COMPLETED / DELIVERED ORDERS (ALWAYS AT BOTTOM)
              ======================================================= */}
          <section data-testid="completed-deliveries-section" style={{ marginTop: 6 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
                borderBottom: "2px solid #e2e8f0",
                paddingBottom: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
                  Completed Deliveries
                </h3>
                <span className="badge green" style={{ fontSize: 11 }}>
                  {completedDeliveries.length}
                </span>
              </div>
              <span className="muted" style={{ fontSize: 12 }}>
                Fulfillment history for this delivery person
              </span>
            </div>

            {completedDeliveries.length === 0 ? (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                  borderRadius: 8,
                  padding: "20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: 13,
                }}
                data-testid="no-completed-deliveries"
              >
                No completed deliveries recorded yet.
              </div>
            ) : (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <div className="table-wrap">
                  <table style={{ margin: 0, width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ width: 44, textAlign: "center" }}>#</th>
                        <th style={{ width: 140 }}>Order / Batch ID</th>
                        <th style={{ width: 160 }}>Client</th>
                        <th style={{ width: 200 }}>Product &amp; Qty</th>
                        <th style={{ minWidth: 180, maxWidth: 260 }}>Destination</th>
                        <th style={{ width: 140 }}>Delivered To</th>
                        <th style={{ width: 170 }}>Delivered Date &amp; Time</th>
                        <th style={{ width: 100, textAlign: "center" }}>Status</th>
                        <th style={{ textAlign: "right", width: 100 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedDeliveries.map((item, idx) => {
                        if (item.isBatch) {
                          const isExpanded = expandedCompletedBatches[item.batch_id];
                          return (
                            <Fragment key={`completed-batch-${item.batch_id}`}>
                              <tr
                                data-testid={`completed-batch-row-${item.batch_id}`}
                                style={{ background: "#f8fafc" }}
                              >
                                <td
                                  style={{
                                    textAlign: "center",
                                    color: "#64748b",
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  {String(idx + 1).padStart(2, "0")}
                                </td>
                                <td>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                    <span className="delivery-group-badge" style={{ fontSize: 11 }}>
                                      <Layers size={11} /> #{item.batch_id}
                                    </span>
                                    <span
                                      className="badge blue"
                                      style={{ fontSize: 10, width: "fit-content" }}
                                    >
                                      Bulk · {item.orders.length} Products
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                                    {item.client_name || item.deliver_to?.company_name || "—"}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ fontSize: 12.5, color: "#1e293b" }}>
                                    {item.orders.length} Products ·{" "}
                                    <b>{(item.totalQuantity || 0).toLocaleString()} pcs</b>
                                  </div>
                                  {item.totalValue ? (
                                    <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 2 }}>
                                      {compactMoney(item.totalValue)}
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: "#475569",
                                      maxWidth: 240,
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      lineHeight: 1.4,
                                    }}
                                    title={item.deliver_to?.address || ""}
                                  >
                                    {item.deliver_to?.address || "Standard client delivery address on file"}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b" }}>
                                    {item.delivered_to || item.deliver_to?.contact_person || "Client"}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>
                                    {formatDeliveredDateTime(
                                      item.delivered_at ||
                                        item.delivery_date ||
                                        item.required_delivery_date
                                    )}
                                  </div>
                                  {item.challan_number && (
                                    <div
                                      style={{
                                        fontSize: 11,
                                        color: "#166534",
                                        marginTop: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                      }}
                                    >
                                      <FileText size={10} /> {item.challan_number}
                                    </div>
                                  )}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span
                                    className="badge green"
                                    style={{ fontSize: 11, padding: "3px 8px", display: "inline-block" }}
                                  >
                                    Delivered
                                  </span>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  <button
                                    type="button"
                                    className="outline-btn compact"
                                    onClick={() => toggleExpandCompletedBatch(item.batch_id)}
                                    data-testid={`toggle-completed-batch-${item.batch_id}`}
                                    style={{ padding: "4px 8px", fontSize: 11 }}
                                  >
                                    {isExpanded ? "Hide" : `Items (${item.orders.length})`}
                                  </button>
                                </td>
                              </tr>
                              {isExpanded &&
                                item.orders.map((subOrd) => (
                                  <tr
                                    key={`sub-completed-${subOrd.order_id}`}
                                    style={{ background: "#ffffff", borderLeft: "3px solid #3b82f6" }}
                                    data-testid={`completed-batch-subrow-${subOrd.order_id}`}
                                  >
                                    <td style={{ textAlign: "center", color: "#94a3b8", fontSize: 11 }}>↳</td>
                                    <td>
                                      <b
                                        className="mono"
                                        style={{ color: "var(--blue)", cursor: "pointer", fontSize: 12 }}
                                        onClick={() => {
                                          if (onOpenOrder) onOpenOrder(subOrd.order_id);
                                          else if (onOpenDelivery) onOpenDelivery(subOrd.order_id);
                                        }}
                                        data-testid={`completed-order-link-${subOrd.order_id}`}
                                      >
                                        #{subOrd.order_id}
                                      </b>
                                    </td>
                                    <td style={{ color: "#64748b", fontSize: 12 }}>—</td>
                                    <td>
                                      <div style={{ fontSize: 12, color: "#1e293b" }}>
                                        {subOrd.product} · <b>{(subOrd.quantity || 0).toLocaleString()} pcs</b>
                                      </div>
                                      <OrderDescriptionSnippet
                                        description={subOrd.description}
                                        maxLength={80}
                                        testId={`completed-sub-description-${subOrd.order_id}`}
                                      />
                                    </td>
                                    <td style={{ fontSize: 11.5, color: "#64748b" }}>
                                      {subOrd.deliver_to?.address || "Same destination"}
                                    </td>
                                    <td style={{ fontSize: 12, color: "#475569" }}>
                                      {subOrd.received_by || subOrd.delivered_to || "Client"}
                                    </td>
                                    <td style={{ fontSize: 11.5, color: "#64748b" }}>
                                      {formatDeliveredDateTime(subOrd.delivered_at || subOrd.delivery_date)}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      <span className="badge green" style={{ fontSize: 10 }}>Delivered</span>
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                      <button
                                        type="button"
                                        className="outline-btn compact"
                                        onClick={() => {
                                          if (onOpenDelivery) onOpenDelivery(subOrd.order_id);
                                          else if (onOpenOrder) onOpenOrder(subOrd.order_id);
                                        }}
                                        data-testid={`open-completed-delivery-${subOrd.order_id}`}
                                        style={{ padding: "3px 6px", fontSize: 11 }}
                                      >
                                        <ExternalLink size={11} /> Details
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                            </Fragment>
                          );
                        }

                        // Standalone order
                        const order = item;
                        return (
                          <tr
                            key={order.order_id}
                            data-testid={`completed-delivery-row-${order.order_id}`}
                          >
                            <td style={{ textAlign: "center", color: "#64748b", fontSize: 12, fontWeight: 500 }}>
                              {String(idx + 1).padStart(2, "0")}
                            </td>
                            <td>
                              <b
                                className="mono"
                                style={{ color: "var(--blue)", cursor: "pointer", fontSize: 13 }}
                                onClick={() => {
                                  if (onOpenOrder) onOpenOrder(order.order_id);
                                  else if (onOpenDelivery) onOpenDelivery(order.order_id);
                                }}
                                data-testid={`completed-order-link-${order.order_id}`}
                              >
                                #{order.order_id}
                              </b>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                                {order.client_name || order.deliver_to?.company_name || "—"}
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: 12.5, color: "#1e293b" }}>
                                {order.product || "Standard Delivery"} · <b>{(order.quantity || 0).toLocaleString()} pcs</b>
                              </div>
                              {order.total_value ? (
                                <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 2 }}>
                                  {compactMoney(order.total_value)}
                                </div>
                              ) : null}
                              <OrderDescriptionSnippet
                                description={order.description}
                                maxLength={80}
                                testId={`completed-order-description-${order.order_id}`}
                              />
                            </td>
                            <td>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#475569",
                                  maxWidth: 240,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  lineHeight: 1.4,
                                }}
                                title={order.deliver_to?.address || ""}
                              >
                                {order.deliver_to?.address || order.delivery_address || "Standard client delivery address on file"}
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b" }}>
                                {order.received_by || order.delivered_to || order.deliver_to?.contact_person || "Client"}
                              </div>
                              {order.delivery_remarks && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#64748b",
                                    marginTop: 2,
                                    maxWidth: 160,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={order.delivery_remarks}
                                >
                                  Note: {order.delivery_remarks}
                                </div>
                              )}
                            </td>
                            <td>
                              <div style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>
                                {formatDeliveredDateTime(order.delivered_at || order.delivery_date || order.required_delivery_date)}
                              </div>
                              {order.challan_number && (
                                <div style={{ fontSize: 11, color: "#166534", marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                  <FileText size={10} /> {order.challan_number}
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span
                                className="badge green"
                                style={{ fontSize: 11, padding: "3px 8px", display: "inline-block" }}
                              >
                                Delivered
                              </span>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                type="button"
                                className="outline-btn compact"
                                onClick={() => {
                                  if (onOpenDelivery) onOpenDelivery(order.order_id);
                                  else if (onOpenOrder) onOpenOrder(order.order_id);
                                }}
                                data-testid={`open-completed-delivery-${order.order_id}`}
                                style={{ padding: "4px 8px", fontSize: 11 }}
                              >
                                <ExternalLink size={12} /> Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Modal: Mark Delivered Confirmation */}
      {completingOrder && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
        >
          <form
            className="modal"
            onSubmit={submitCompleteDelivery}
            data-testid="complete-delivery-modal"
          >
            <div className="modal-head">
              <div>
                <div className="small-label">CONFIRM DELIVERY COMPLETION</div>
                <h2>Order #{completingOrder.order_id}</h2>
                <p className="muted">{completingOrder.client_name}</p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setCompletingOrder(null)}
              >
                <X size={18} />
              </button>
            </div>

            {!completingOrder.has_challan ? (
              <div
                style={{
                  margin: "12px 0 6px",
                  padding: "10px 14px",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
                data-testid="modal-challan-required-alert"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <AlertTriangle size={15} color="#b45309" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: "#92400e", fontWeight: 600 }}>
                    Please upload a challan before marking this delivery as delivered.
                  </span>
                </div>
                <div>
                  <input
                    type="file"
                    id={`modal-challan-upload-${completingOrder.order_id}`}
                    accept=".pdf,image/*,.doc,.docx"
                    style={{ display: "none" }}
                    disabled={uploadBusyOrderId === completingOrder.order_id}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleUploadChallan(completingOrder, e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  <label
                    htmlFor={`modal-challan-upload-${completingOrder.order_id}`}
                    className="primary-btn compact"
                    style={{
                      background: "#d97706",
                      borderColor: "#d97706",
                      color: "#ffffff",
                      cursor: uploadBusyOrderId === completingOrder.order_id ? "wait" : "pointer",
                      fontSize: 12,
                      padding: "5px 12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                    data-testid="modal-upload-challan-btn"
                  >
                    <Upload size={13} />
                    {uploadBusyOrderId === completingOrder.order_id ? "Uploading…" : "Upload Challan Now"}
                  </label>
                </div>
              </div>
            ) : (
              <div
                style={{
                  margin: "12px 0 6px",
                  padding: "10px 14px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
                data-testid="modal-challan-attached-box"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#166534" }}>
                  <FileText size={15} />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                    Challan attached: <b>{completingOrder.challan_number || "Document on file"}</b>
                  </span>
                </div>
                <div>
                  <input
                    type="file"
                    id={`modal-challan-reupload-${completingOrder.order_id}`}
                    accept=".pdf,image/*,.doc,.docx"
                    style={{ display: "none" }}
                    disabled={uploadBusyOrderId === completingOrder.order_id}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleUploadChallan(completingOrder, e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  <label
                    htmlFor={`modal-challan-reupload-${completingOrder.order_id}`}
                    style={{
                      fontSize: 11.5,
                      color: "#4b5563",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    title="Re-upload Challan"
                  >
                    Re-upload
                  </label>
                </div>
              </div>
            )}

            <div className="form-grid" style={{ marginTop: 14 }}>
              <label className="full">
                Received By *
                <input
                  required
                  value={receivedByInput}
                  onChange={(e) => setReceivedByInput(e.target.value)}
                  placeholder="e.g. Vikas Singh, Security Officer, Store Manager"
                  data-testid="delivery-received-by-input"
                />
              </label>

              <label className="full">
                Delivery Remarks / POD Notes
                <textarea
                  rows={3}
                  value={deliveryRemarksInput}
                  onChange={(e) => setDeliveryRemarksInput(e.target.value)}
                  placeholder="e.g. Goods received in intact condition, signed physical challan."
                  data-testid="delivery-remarks-input"
                />
              </label>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setCompletingOrder(null)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                disabled={statusBusy || !completingOrder.has_challan}
                style={{
                  flex: 1,
                  background: !completingOrder.has_challan ? "#94a3b8" : "#16a34a",
                  borderColor: !completingOrder.has_challan ? "#94a3b8" : "#16a34a",
                  color: "#ffffff",
                  cursor: !completingOrder.has_challan ? "not-allowed" : "pointer",
                }}
                data-testid="confirm-delivered-button"
                title={
                  !completingOrder.has_challan
                    ? "Please upload a challan before marking this delivery as delivered."
                    : "Confirm delivery"
                }
              >
                {statusBusy ? "Confirming…" : "Confirm Delivered"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal: Reassign Delivery Person */}
      {reassigningOrder && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
        >
          <form
            className="modal"
            onSubmit={submitReassign}
            data-testid="reassign-delivery-modal"
          >
            <div className="modal-head">
              <div>
                <div className="small-label">REASSIGN DELIVERY PERSON</div>
                <h2>Order #{reassigningOrder.order_id}</h2>
                <p className="muted">
                  Current: {reassigningOrder.assigned_delivery_person || "Unassigned"}
                </p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setReassigningOrder(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <label className="full">
                Select Delivery Agent *
                <select
                  required
                  value={reassignAgentId}
                  onChange={(e) => setReassignAgentId(e.target.value)}
                  data-testid="reassign-agent-select"
                >
                  {deliveryAgents.length === 0 ? (
                    <option value="" disabled>No Delivery Person available</option>
                  ) : (
                    <option value="">-- Choose Delivery Person --</option>
                  )}
                  {deliveryAgents.map((a) => (
                    <option key={a.user_id} value={a.user_id}>
                      {a.name} ({a.phone || "No phone"})
                    </option>
                  ))}
                </select>
              </label>

              <div
                style={{
                  gridColumn: "1 / -1",
                  fontSize: 12.5,
                  color: "#475569",
                  background: "#f8fafc",
                  padding: "10px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--line)",
                  lineHeight: 1.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 6,
                }}
              >
                <Info size={14} style={{ flexShrink: 0, marginTop: 2, color: "#2563eb" }} />
                <span>
                  When assigned, this order will be moved from {personData.displayName}&apos;s
                  queue to the selected driver&apos;s queue.
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setReassigningOrder(null)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                disabled={reassignBusy || !reassignAgentId}
                style={{ flex: 1 }}
                data-testid="confirm-reassign-button"
              >
                {reassignBusy ? "Assigning…" : "Save Assignment"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal: Mark Bulk Group Delivered Confirmation */}
      {completingGroup && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
        >
          <form
            className="modal"
            onSubmit={submitCompleteGroupDelivery}
            data-testid="complete-group-delivery-modal"
            style={{ maxWidth: 640 }}
          >
            <div className="modal-head">
              <div>
                <div className="small-label">CONFIRM BULK GROUP DELIVERY</div>
                <h2>Batch #{completingGroup.batch_id}</h2>
                <p className="muted">
                  Client: <b>{completingGroup.client_name}</b> · {completingGroup.activeOrders.length} Products to Deliver
                </p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setCompletingGroup(null)}
              >
                <X size={18} />
              </button>
            </div>

            {/* Challan Status Alert / Upload in Group Completion Modal */}
            {!completingGroup.has_challan ? (
              <div
                style={{
                  margin: "12px 0 10px",
                  padding: "10px 14px",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
                data-testid="modal-group-challan-required-alert"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <AlertTriangle size={15} color="#b45309" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: "#92400e", fontWeight: 600 }}>
                    Please upload a challan before marking this delivery as delivered.
                  </span>
                </div>
                <div>
                  <input
                    type="file"
                    id={`modal-group-challan-upload-${completingGroup.batch_id}`}
                    accept=".pdf,image/*,.doc,.docx"
                    style={{ display: "none" }}
                    disabled={
                      uploadBusyOrderId ===
                      (completingGroup.orders?.[0]?.order_id || completingGroup.activeOrders?.[0]?.order_id)
                    }
                    onChange={(e) => {
                      const targetOrder = completingGroup.orders?.[0] || completingGroup.activeOrders?.[0];
                      if (e.target.files?.[0] && targetOrder) {
                        handleUploadChallan(targetOrder, e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  <label
                    htmlFor={`modal-group-challan-upload-${completingGroup.batch_id}`}
                    className="primary-btn compact"
                    style={{
                      background: "#d97706",
                      borderColor: "#d97706",
                      color: "#ffffff",
                      cursor:
                        uploadBusyOrderId ===
                        (completingGroup.orders?.[0]?.order_id || completingGroup.activeOrders?.[0]?.order_id)
                          ? "wait"
                          : "pointer",
                      fontSize: 12,
                      padding: "5px 12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                    data-testid="modal-group-upload-challan-btn"
                  >
                    <Upload size={13} />
                    {uploadBusyOrderId ===
                    (completingGroup.orders?.[0]?.order_id || completingGroup.activeOrders?.[0]?.order_id)
                      ? "Uploading…"
                      : "Upload Challan Now"}
                  </label>
                </div>
              </div>
            ) : (
              <div
                style={{
                  margin: "12px 0 10px",
                  padding: "10px 14px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
                data-testid="modal-group-challan-attached-box"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#166534" }}>
                  <FileText size={15} />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                    Challan attached: <b>{completingGroup.challan_number || `Batch #${completingGroup.batch_id}`}</b>
                  </span>
                </div>
                <div>
                  <input
                    type="file"
                    id={`modal-group-challan-reupload-${completingGroup.batch_id}`}
                    accept=".pdf,image/*,.doc,.docx"
                    style={{ display: "none" }}
                    disabled={
                      uploadBusyOrderId ===
                      (completingGroup.orders?.[0]?.order_id || completingGroup.activeOrders?.[0]?.order_id)
                    }
                    onChange={(e) => {
                      const targetOrder = completingGroup.orders?.[0] || completingGroup.activeOrders?.[0];
                      if (e.target.files?.[0] && targetOrder) {
                        handleUploadChallan(targetOrder, e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  <label
                    htmlFor={`modal-group-challan-reupload-${completingGroup.batch_id}`}
                    style={{
                      fontSize: 11.5,
                      color: "#4b5563",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    title="Re-upload Challan"
                  >
                    Re-upload
                  </label>
                </div>
              </div>
            )}

            {/* Product summary before completing */}
            <div
              style={{
                margin: "12px 0 6px",
                border: "1px solid var(--line)",
                borderRadius: 8,
                overflow: "hidden",
                background: "#f8fafc",
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#334155",
                  background: "#f1f5f9",
                  borderBottom: "1px solid var(--line)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>PRODUCTS INCLUDED IN THIS DELIVERY ({completingGroup.activeOrders.length})</span>
                <span className="badge blue" style={{ fontSize: 10.5 }}>
                  {completingGroup.deliver_to?.company_name || completingGroup.client_name}
                </span>
              </div>
              <div style={{ maxHeight: 180, overflowY: "auto" }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid var(--line)", color: "#64748b" }}>
                      <th style={{ padding: "6px 12px", textAlign: "left" }}>Order ID</th>
                      <th style={{ padding: "6px 12px", textAlign: "left" }}>Product</th>
                      <th style={{ padding: "6px 12px", textAlign: "right" }}>Quantity</th>
                      <th style={{ padding: "6px 12px", textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completingGroup.activeOrders.map((ord) => (
                      <tr key={ord.order_id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                        <td style={{ padding: "6px 12px", fontFamily: "monospace", color: "var(--blue)", fontWeight: 600 }}>
                          #{ord.order_id}
                        </td>
                        <td style={{ padding: "6px 12px", color: "#1e293b", fontWeight: 500 }}>
                          {ord.product}
                        </td>
                        <td style={{ padding: "6px 12px", textAlign: "right" }}>
                          <b>{(ord.quantity || 0).toLocaleString()}</b> units
                        </td>
                        <td style={{ padding: "6px 12px", textAlign: "center" }}>
                          <span className="badge blue" style={{ fontSize: 10 }}>
                            {ord.delivery_status || "Out for Delivery"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <label className="full">
                Received By *
                <input
                  required
                  value={receivedByInput}
                  onChange={(e) => setReceivedByInput(e.target.value)}
                  placeholder="e.g. Vikas Singh, Store Manager, Security Office"
                  data-testid="group-delivery-received-by-input"
                />
              </label>

              <label className="full">
                Delivery Remarks / POD Notes
                <textarea
                  rows={3}
                  value={deliveryRemarksInput}
                  onChange={(e) => setDeliveryRemarksInput(e.target.value)}
                  placeholder="e.g. All cartons delivered and inspected in sound condition."
                  data-testid="group-delivery-remarks-input"
                />
              </label>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setCompletingGroup(null)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                disabled={statusBusy || !completingGroup.has_challan}
                style={{
                  flex: 1,
                  background: !completingGroup.has_challan ? "#94a3b8" : "#16a34a",
                  borderColor: !completingGroup.has_challan ? "#94a3b8" : "#16a34a",
                  color: "#ffffff",
                  cursor: !completingGroup.has_challan ? "not-allowed" : "pointer",
                  fontWeight: 700,
                }}
                data-testid="confirm-group-delivered-button"
                title={
                  !completingGroup.has_challan
                    ? "Please upload a challan before marking this delivery as delivered."
                    : "Confirm delivery for all products in this group"
                }
              >
                {statusBusy ? "Confirming…" : `Confirm All (${completingGroup.activeOrders.length}) Delivered`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal: Reassign Bulk Group */}
      {reassigningGroup && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10001 }}
        >
          <form
            className="modal"
            onSubmit={submitReassignGroup}
            data-testid="reassign-group-modal"
            style={{ maxWidth: 540 }}
          >
            <div className="modal-head">
              <div>
                <div className="small-label">REASSIGN BULK GROUP STOP</div>
                <h2>Batch #{reassigningGroup.batch_id}</h2>
                <p className="muted">
                  Current: {personData.displayName} · {reassigningGroup.orders.length} Products
                </p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setReassigningGroup(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div
              style={{
                margin: "12px 0 6px",
                padding: "10px 14px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 6,
                fontSize: 12.5,
                color: "#166534",
                lineHeight: 1.5,
              }}
            >
              Reassigning this batch will move all <b>{reassigningGroup.orders.length} products</b> together to the selected delivery driver&apos;s queue, preserving their grouped stop association.
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <label className="full">
                Select Delivery Agent *
                <select
                  required
                  value={reassignAgentId}
                  onChange={(e) => setReassignAgentId(e.target.value)}
                  data-testid="reassign-group-agent-select"
                >
                  {deliveryAgents.length === 0 ? (
                    <option value="" disabled>No Delivery Person available</option>
                  ) : (
                    <option value="">-- Choose Delivery Person --</option>
                  )}
                  {deliveryAgents.map((a) => (
                    <option key={a.user_id} value={a.user_id}>
                      {a.name} ({a.phone || "No phone"})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setReassigningGroup(null)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                disabled={reassignBusy || !reassignAgentId}
                style={{ flex: 1 }}
                data-testid="confirm-reassign-group-button"
              >
                {reassignBusy ? "Assigning Group…" : "Reassign Entire Group"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
